<template>
  <v-container fluid class="fill-height">
    <template v-if="MediaCategories">
      <v-col cols="2" class="fill-height" v-if="$vuetify.breakpoint.mdAndUp">
        <v-row class="fill-height" align="center" justify="center" :style="{position: 'fixed', maxWidth: '16.6666666667%'}">
          <v-col cols="12" class="pl-10">
            <GenericMenuItem
              class="menu-item"
              v-for="(item, catIndex) in MediaCategories.items"
              :dot-offset-x="-10"
              :dot-offset-y="10"
              :key="item.id"
              :to="{params: {categorySlug: item._slug, mediaSlug: null}}"
              @click.native.prevent="$vuetify.goTo('#' + item._slug)"
              replace
              >
                <CharSplitText :style="{'--appear-delay': `${catIndex * 200}ms`}" :text="item[`${$language.current}Title`]" />
            </GenericMenuItem>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="10" class="pt-md-16 mt-md-16">
        <v-row v-for="(category, catIndex) in MediaCategories.items" :key="category.id" v-intersect="{handler: (entries, observer, isIntersecting) => onCategoryIntersect(category, catIndex, isIntersecting), options: {threshold: [0.8]}}">
          <v-col cols="12"><h2 class="py-12" :id="category._slug"><CharSplitText :style="{'--appear-delay': `${catIndex * 200}ms`}" :text="category[`${$language.current}Title`]" /></h2></v-col>
          <ApolloQuery :tag="null" :query="localizedQueries[$language.current].listMediaForCategory" :variables="{status: 'PUBLISHED', slug: category._slug, sortOn: 'publishedDate' }" v-slot="{ result: { data } }">
            <transition appear name="from-below" mode="out-in">
              <ContentGallery2 :style="{'--appear-delay': `${catIndex * 200}ms`, width: $vuetify.breakpoint.mdAndUp ? '91.666666666%' : '100%'}" class="offset-md-1" :cols="$vuetify.breakpoint.mdAndUp ? 4 : 8" v-if="data" :data="data.MediaCategory._referents.MediaContentReferents.items" :page-numbers="false" :height="galleryHeight">
                <template v-slot:generic="{ galleryElement }">
                  <router-link :aria-label="galleryElement[`${$language.current}Title`]" v-if="galleryElement.youtubeURL" class="d-block" :to="{params: {categorySlug: category._slug, mediaSlug: galleryElement._slug}}"><seo-v-img :src="`https://img.youtube.com/vi/${getVideoId(galleryElement.youtubeURL).id}/0.jpg`" :aspect-ratio="16/9" cover /></router-link>
                  <a v-else :aria-label="galleryElement[`${$language.current}Title`]" class="d-block" :href="galleryElement[`${$language.current}GenericURL`]" target="_blank"><seo-v-img :src="`/media/${galleryElement.genericThumbnail.id}`" :aspect-ratio="16/9" cover /></a>
                  <div class="date">{{new Date(galleryElement.publishedDate).toLocaleString($language.current, { year: 'numeric', month: 'long', day: 'numeric' })}}</div>
                  <h3>{{galleryElement[`${$language.current}Title`]}}</h3>
                  <DiscoverButton v-if="galleryElement.youtubeURL" :to="{params: {categorySlug: category._slug, mediaSlug: galleryElement._slug}}" @click.native="$vuetify.goTo('#' + category._slug)" />
                  <DiscoverButton v-else :href="galleryElement[`${$language.current}GenericURL`]" rel="noopener" />
                </template>
              </ContentGallery2>
              <v-row v-else :style="{height: `${galleryHeight}px`}" class="flex-nowrap fill-height" align="center" justify="center">
                <v-col class="shrink"><LoadingSpinner /></v-col>
              </v-row>
            </transition>
          </ApolloQuery>
        </v-row>
      </v-col>
    </template>
    <v-row v-else class="fill-height" align="center" justify="center">
      <v-col class="shrink"><LoadingSpinner /></v-col>
    </v-row>

    <v-dialog :value="popupOpen" @input="onPopupValueChange">
      <v-responsive :aspect-ratio="16/9" width="100%" max-height="90vh">
        <ApolloQuery :skip="!mediaSlug" class="fill-height" :query="localizedQueries[$language.current].getMedia" :variables="{status: 'PUBLISHED', slug: mediaSlug }" v-slot="{ result: { data } }">
          <v-sheet class="fill-height" color="#f4f4f8">
            <transition mode="out-in">
              <iframe v-if="!$root.platform.prerenderer && data && data.MediaContent && iframeVisible" width="100%" height="100%" :src="`https://www.youtube.com/embed/${getVideoId(data.MediaContent.youtubeURL).id}`" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen />
              <v-row v-else class="fill-height" align="center" justify="center">
                <LoadingSpinner />
              </v-row>
            </transition>
          </v-sheet>
        </ApolloQuery>
      </v-responsive>
    </v-dialog>
  </v-container>
</template>

<script>
import GetMediaContent from '@/graphql/GetMediaContent.gql'

import ListMediaForCategoryEN from '@/graphql/ListMediaForCategory_EN.gql'
import ListMediaCategoryEN from '@/graphql/ListMediaCategory_EN.gql'

import ListMediaForCategoryIT from '@/graphql/ListMediaForCategory_IT.gql'
import ListMediaCategoryIT from '@/graphql/ListMediaCategory_IT.gql'
import getVideoId from 'get-video-id'
import GenericMenuItem from '@/atoms/GenericMenuItem'
import ContentGallery2 from '@/atoms/ContentGallery2'
import { Viewport } from '@monogrid/vue-lib'
import DiscoverButton from '@/atoms/DiscoverButton'
import CharSplitText from '@/atoms/CharSplitText'
import LoadingSpinner from '@/atoms/LoadingSpinner'
import delay from 'lodash/delay'

export default {
  name: 'MediaPage',
  mixins: [Viewport],

  components: {
    ContentGallery2,
    GenericMenuItem,
    DiscoverButton,
    CharSplitText,
    LoadingSpinner
  },

  props: {
    categorySlug: {
      type: String,
      required: false
    },
    mediaSlug: {
      type: String,
      required: false
    }
  },

  data () {
    return {
      popupOpen: false,
      iframeVisible: false,
      localizedQueries: {
        en: {
          getMedia: GetMediaContent,
          listMediaForCategory: ListMediaForCategoryEN,
          listMediaCategories: ListMediaCategoryEN
        },
        it: {
          getMedia: GetMediaContent,
          listMediaForCategory: ListMediaForCategoryIT,
          listMediaCategories: ListMediaCategoryIT
        }
      }
    }
  },

  methods: {
    getVideoId,
    onCategoryIntersect (category, catIndex, isIntersecting) {
      if (isIntersecting) {
        if (this.categorySlug !== category._slug) this.$router.replace({ params: { categorySlug: category._slug } })
      }
    },
    onPopupValueChange (e) {
      if (!e) {
        this.$router.push({ params: { mediaSlug: null } })
      }
    }
  },

  watch: {
    mediaSlug (value) {
      this.popupOpen = !!value
      if (!value) {
        delay(() => {
          this.iframeVisible = false
        }, 300)
      } else {
        this.iframeVisible = true
      }
    }
  },
  mounted () {
    this.popupOpen = this.iframeVisible = !!this.mediaSlug
  },

  apollo: {

    MediaCategories: {
      variables: { status: 'PUBLISHED', sortOn: 'priority' },
      query () { return this.localizedQueries[this.$language.current].listMediaCategories },
      async result () {
        await this.$nextTick()
        if (this.categorySlug) this.$vuetify.goTo('#' + this.categorySlug)
        else this.$root.$emit('scroll-to-saved-position')
      }
    }
  },

  computed: {
    galleryHeight () {
      if (this.$vuetify.breakpoint.mdAndDown) return this.viewPort.height * 0.6
      return 550
    }
  },

  metaInfo () {
    const pageName = 'media'
    const imageMeta = JSON.parse(this.$root.siteSettings[`${pageName}PageShareImage`].metaData)
    return {
      // home page browser title
      title: this.$root.siteSettings[`${pageName}PageTitle`],
      meta: [
        { vmid: 'description', name: 'description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${this.$root.siteSettings[`${pageName}PageTitle`]} | ${this.$root.siteSettings.siteTitle}` },
        { vmid: 'ogdescription', property: 'og:description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogimage', property: 'og:image', content: `https://${this.$root.process.env.SERVICE_TARGET_DOMAIN}/media/${this.$root.siteSettings[`${pageName}PageShareImage`].id}` },
        { vmid: 'ogimagetype', property: 'og:image:type', content: this.$root.siteSettings[`${pageName}PageShareImage`].mimeType },
        { vmid: 'ogimageheight', property: 'og:image:width', content: imageMeta['image-width'] },
        { vmid: 'ogimagewidth', property: 'og:image:height', content: imageMeta['image-height'] }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>

.category {
  display: block;
}

.date {
  font-family: $body-font-family;
  font-size: 0.875rem;
  color: $c-pink;
  margin: 0.6rem 0;
}

h2 {
  font-family: map-get(map-get($headings, 'h3'), 'font-family');
  font-weight: map-get(map-get($headings, 'h3'), 'weight');
  letter-spacing: map-get(map-get($headings, 'h3'), 'letter-spacing');
  line-height: map-get(map-get($headings, 'h3'), 'line-height');
  font-size: map-get(map-get($headings, 'h3'), 'size');

  @include breakpoint('sm-and-down') {
    font-size: map-get(map-get($headings, 'h3'), 'size') - 0.6rem;
  }
}

h3 {
  font-family: map-get(map-get($headings, 'h4'), 'font-family');
  font-weight: map-get(map-get($headings, 'h4'), 'weight');
  letter-spacing: map-get(map-get($headings, 'h4'), 'letter-spacing');
  line-height: map-get(map-get($headings, 'h4'), 'line-height');
  font-size: map-get(map-get($headings, 'h4'), 'size');

  @include breakpoint('sm-and-down') {
    font-size: map-get(map-get($headings, 'h4'), 'size') - 0.6rem;
  }
}

.menu-item {
  font-size: 1.7rem;
}

::v-deep {
  .go-prev-image,
  .go-next-image {
    top: -40px !important;
  }

  .go-prev-image {
    right: 70px !important;
  }

  .go-next-image {
    right: 20px !important;
  }
}

</style>
